import {
  MAX_PIN_ATTEMPTS,
  PIN_OK_STATE,
  isLocked,
  normalizeUsername,
  registerPinFailure,
  uuidv7,
  verifySecret,
} from '@pos/core';
import type { OutboxOp, SyncTable } from '@pos/sync';
import { OWNER_ONLY_TABLES, type LocalDb } from './db.ts';
import type { LocalCredential, MetaMap } from './types.ts';

/** Quién escribe: se guarda en cada operación de la cola. */
export interface WriteActor {
  user_id: string;
  business_id: string;
  device_id: string | null;
}

function newOp(
  actor: WriteActor,
  table: SyncTable,
  kind: OutboxOp['kind'],
  rowId: string,
  payload: Record<string, unknown>,
  base?: Record<string, unknown>,
): OutboxOp {
  return {
    op_id: uuidv7(),
    business_id: actor.business_id,
    table,
    kind,
    row_id: rowId,
    payload,
    ...(base ? { base } : {}),
    created_at: new Date().toISOString(),
    created_by: actor.user_id,
    device_id: actor.device_id,
    status: 'pending',
    attempts: 0,
    next_attempt_at: 0,
  };
}

/** Lo que el dispositivo tenía en cada campo antes del cambio (para detectar conflictos). */
function baseOf(current: object, changes: Record<string, unknown>): Record<string, unknown> {
  const row = current as Record<string, unknown>;
  return Object.fromEntries(Object.keys(changes).map((k) => [k, row[k] ?? null]));
}

/**
 * Modifica campos de una fila local y encola el cambio, en una sola transacción:
 * o quedan las dos cosas, o ninguna.
 */
export async function patchRow(
  db: LocalDb,
  table: SyncTable,
  id: string,
  changes: Record<string, unknown>,
  actor: WriteActor,
): Promise<OutboxOp> {
  if (Object.keys(changes).length === 0) throw new Error('No hay cambios para guardar.');
  return db.transaction('rw', db.syncTable(table), db.outbox, async () => {
    const target = db.syncTable(table);
    const current = await target.get(id);
    if (!current) throw new Error(`No existe el registro ${table}/${id} en este dispositivo.`);
    await target.update(id, changes);
    const op = newOp(actor, table, 'patch', id, changes, baseOf(current, changes));
    op.seq = await db.outbox.add(op);
    return op;
  });
}

/** Inserta una fila local nueva y encola su alta (idempotente por id en el servidor). */
export async function insertRow<T extends { id: string }>(
  db: LocalDb,
  table: SyncTable,
  row: T,
  actor: WriteActor,
): Promise<OutboxOp> {
  return db.transaction('rw', db.syncTable(table), db.outbox, async () => {
    await db.syncTable(table).add(row);
    const op = newOp(actor, table, 'insert', row.id, row as unknown as Record<string, unknown>);
    op.seq = await db.outbox.add(op);
    return op;
  });
}

export type LocalChange =
  | { table: SyncTable; kind: 'insert'; row: { id: string } & Record<string, unknown> }
  | { table: SyncTable; kind: 'patch'; id: string; changes: Record<string, unknown> };

/**
 * Aplica varios cambios (altas y modificaciones, en varias tablas) en UNA transacción local
 * y los encola en orden. Ej.: un producto nuevo con sus variantes y costos; una importación;
 * un aumento masivo. O quedan todos, o ninguno.
 */
export async function applyLocalChanges(db: LocalDb, changes: LocalChange[], actor: WriteActor): Promise<number> {
  if (changes.length === 0) return 0;
  const tables = [...new Set(changes.map((c) => c.table))].map((t) => db.syncTable(t));
  return db.transaction('rw', [...tables, db.outbox], async () => {
    const ops: OutboxOp[] = [];
    // Filas dadas de alta en este mismo lote: su modificación no lleva base (si la fila ya
    // existía en el servidor, el alta no hace nada y el patch tiene que dejar el valor igual).
    const inserted = new Set<string>();
    for (const change of changes) {
      const target = db.syncTable(change.table);
      if (change.kind === 'insert') {
        await target.add(change.row);
        inserted.add(`${change.table}:${change.row.id}`);
        ops.push(newOp(actor, change.table, 'insert', change.row.id, change.row));
      } else {
        if (Object.keys(change.changes).length === 0) continue;
        const current = await target.get(change.id);
        if (!current) throw new Error(`No existe el registro ${change.table}/${change.id} en este dispositivo.`);
        await target.update(change.id, change.changes);
        const base = inserted.has(`${change.table}:${change.id}`) ? undefined : baseOf(current, change.changes);
        ops.push(newOp(actor, change.table, 'patch', change.id, change.changes, base));
      }
    }
    await db.outbox.bulkAdd(ops);
    return ops.length;
  });
}

/** Borra los datos que solo ve el dueño (al cerrar su sesión en un dispositivo compartido). */
export async function clearOwnerOnlyData(db: LocalDb): Promise<void> {
  await db.transaction('rw', [...OWNER_ONLY_TABLES.map((t) => db.syncTable(t)), db.cursors], async () => {
    for (const table of OWNER_ONLY_TABLES) {
      await db.syncTable(table).clear();
      await db.cursors.filter((c) => c.key.endsWith(`:${table}`)).delete();
    }
  });
}

// ---------------------------------------------------------------------------
// Meta (identidad del dispositivo, sesión actual)
// ---------------------------------------------------------------------------

export async function getMeta<K extends keyof MetaMap>(db: LocalDb, key: K): Promise<MetaMap[K] | undefined> {
  const entry = await db.meta.get(key);
  return entry?.value as MetaMap[K] | undefined;
}

export async function setMeta<K extends keyof MetaMap>(db: LocalDb, key: K, value: MetaMap[K]): Promise<void> {
  await db.meta.put({ key, value } as never);
}

export async function deleteMeta(db: LocalDb, key: keyof MetaMap): Promise<void> {
  await db.meta.delete(key);
}

// ---------------------------------------------------------------------------
// Credenciales locales (login offline)
// ---------------------------------------------------------------------------

export function normalizeLoginId(kind: LocalCredential['kind'], loginId: string): string {
  return kind === 'password' ? loginId.trim().toLowerCase() : normalizeUsername(loginId);
}

export function credentialId(kind: LocalCredential['kind'], businessId: string, loginId: string): string {
  return `${kind}:${businessId}:${normalizeLoginId(kind, loginId)}`;
}

export async function saveCredential(
  db: LocalDb,
  credential: Omit<LocalCredential, 'id' | 'updated_at' | 'failed_attempts' | 'locked_until'>,
): Promise<void> {
  const loginId = normalizeLoginId(credential.kind, credential.login_id);
  await db.credentials.put({
    ...credential,
    ...PIN_OK_STATE,
    login_id: loginId,
    id: credentialId(credential.kind, credential.business_id, loginId),
    updated_at: new Date().toISOString(),
  });
}

export type LocalVerifyResult =
  | { status: 'ok'; credential: LocalCredential }
  | { status: 'missing' }
  | { status: 'invalid'; remaining: number }
  | { status: 'locked'; lockedUntil: string };

/**
 * Verifica una contraseña o PIN contra el verificador guardado en este dispositivo, con el
 * mismo límite que el servidor: tras MAX_PIN_ATTEMPTS fallos se bloquea PIN_LOCK_MINUTES.
 * Sin esto, sin conexión se podría probar PINs indefinidamente desde la pantalla.
 */
export async function verifyLocalCredential(
  db: LocalDb,
  kind: LocalCredential['kind'],
  businessId: string,
  loginId: string,
  secret: string,
  now: Date = new Date(),
): Promise<LocalVerifyResult> {
  const credential = await findCredential(db, kind, businessId, loginId);
  if (!credential) return { status: 'missing' };
  const state = { failed_attempts: credential.failed_attempts ?? 0, locked_until: credential.locked_until ?? null };
  if (isLocked(state, now)) return { status: 'locked', lockedUntil: state.locked_until! };

  if (await verifySecret(secret, credential.verifier)) {
    if (state.failed_attempts || state.locked_until) await db.credentials.update(credential.id, PIN_OK_STATE);
    return { status: 'ok', credential };
  }
  const next = registerPinFailure(state, now);
  await db.credentials.update(credential.id, next);
  if (isLocked(next, now)) return { status: 'locked', lockedUntil: next.locked_until! };
  return { status: 'invalid', remaining: MAX_PIN_ATTEMPTS - next.failed_attempts };
}

export async function findCredential(
  db: LocalDb,
  kind: LocalCredential['kind'],
  businessId: string,
  loginId: string,
): Promise<LocalCredential | undefined> {
  return db.credentials.get(credentialId(kind, businessId, loginId));
}

/** Borra todos los datos del dispositivo (al quitarle la habilitación). */
export async function wipeDevice(db: LocalDb): Promise<void> {
  await db.transaction('rw', db.tables, async () => {
    await Promise.all(db.tables.map((t) => t.clear()));
  });
}
